/**
 * Kamerabild mit Strichcode-Erkennung. Die Rückkamera wird bevorzugt; ein
 * erkannter Code beendet die Aufnahme. Ohne Kamera, ohne Erlaubnis oder ohne
 * sichere Verbindung (die Kamera braucht HTTPS) erscheint ein Hinweis – die
 * Eingabe von Hand funktioniert immer.
 */
import { useEffect, useRef, useState } from 'react';
import { createCodeReader, normalizeCode } from '../../lib/barcode.ts';
import { texts } from '../../texts/de.ts';

/** Abstand zwischen zwei Leseversuchen. */
const READ_INTERVAL_MS = 180;

interface BarcodeScannerProps {
  readonly onCode: (code: string) => void;
}

/** Zustand der Kamera. */
type CameraState = 'starting' | 'running' | 'unavailable' | 'denied' | 'insecure';

/** Kamerabild, das Codes erkennt. */
export function BarcodeScanner({ onCode }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<CameraState>('starting');
  // Der neueste Rückruf, ohne die Kamera bei jedem Rendern neu zu starten.
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    if (!window.isSecureContext) {
      setState('insecure');
      return undefined;
    }
    if (navigator.mediaDevices?.getUserMedia === undefined) {
      setState('unavailable');
      return undefined;
    }
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;

    const stop = () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        const video = videoRef.current;
        if (stopped || video === null) {
          stop();
          return;
        }
        video.srcObject = stream;
        await video.play();
        const reader = await createCodeReader();
        setState('running');
        const tick = async () => {
          if (stopped) {
            return;
          }
          if (video.readyState >= 2) {
            const codes = await reader.read(video).catch(() => []);
            const code = codes.map(normalizeCode).find((entry) => entry !== null);
            if (code !== undefined && !stopped) {
              navigator.vibrate?.(60);
              stop();
              onCodeRef.current(code);
              return;
            }
          }
          timer = window.setTimeout(() => void tick(), READ_INTERVAL_MS);
        };
        void tick();
      } catch (error) {
        const name = error instanceof DOMException ? error.name : '';
        setState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
        stop();
      }
    })();
    return stop;
  }, []);

  const message =
    state === 'insecure'
      ? texts.scan.cameraInsecure
      : state === 'denied'
        ? texts.scan.cameraDenied
        : state === 'unavailable'
          ? texts.scan.cameraUnavailable
          : null;

  return message !== null ? (
    <p className="rounded-xl bg-surface-sunken px-3 py-3 text-sm text-content-muted">{message}</p>
  ) : (
    <div className="relative overflow-hidden rounded-2xl bg-black">
      <video ref={videoRef} muted playsInline className="block aspect-[4/3] w-full object-cover" />
      {/* Zielrahmen: dorthin gehört der Strichcode. */}
      <div className="pointer-events-none absolute inset-x-[12%] top-1/2 h-[34%] -translate-y-1/2 rounded-xl border-2 border-white/90 shadow-[0_0_0_9999px_rgb(0_0_0/0.35)]" />
      <p className="absolute inset-x-0 bottom-2 text-center text-xs font-medium text-white drop-shadow">
        {state === 'starting' ? texts.scan.cameraStarting : texts.scan.aim}
      </p>
    </div>
  );
}
